import {Box} from "@mui/material";
import React from "react";
import {useTranslation} from "react-i18next";
import {NEGATIVE_COLOR, NEUTRAL_COLOR, POSITIVE_COLOR} from "../../insights/kit/insightsColors";
import {ElementChip} from "./base/ElementChip";

interface SurveyScoreElementProps {
    /** The response's answer quality on 0–100. Nothing is drawn without one: an absent score is not a low one. */
    score: number | null | undefined;
    asPrint?: boolean;
}

/*
    Read with the sentiment colours the module already uses beside a reply: a 10/10 lands on 100,
    an 8/10 on 78, a 5/10 on 44 - so the bands split at roughly "eight and up", "the middle", "below".
*/
const POSITIVE_FROM = 75;
const NEUTRAL_FROM = 50;

/** A response's score is a whole number on every surface - dialog, lists, print/PDF/Word, spreadsheets. */
export const roundSurveyScore = (score: number) => Math.round(score);

export const formatSurveyScore = (score: number) => String(roundSurveyScore(score));

const scoreColor = (score: number) => (
    score >= POSITIVE_FROM ? POSITIVE_COLOR : score >= NEUTRAL_FROM ? NEUTRAL_COLOR : NEGATIVE_COLOR
);

export function SurveyScoreElement({score, asPrint = false}: SurveyScoreElementProps) {
    const {t} = useTranslation();

    if (score === null || score === undefined || !Number.isFinite(score)) return null;

    const text = `${t('Score')}: ${formatSurveyScore(score)}`;

    if (asPrint) {
        return <>{text}</>;
    }

    return (
        <Box sx={{display: "inline-flex", maxWidth: "100%"}}>
            <ElementChip
                text={text}
                tooltipContent={t('Answer quality of this response on 0 to 100: the mean of its rated answers.')}
                startIcon={
                    <Box
                        component="span"
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: scoreColor(score),
                            flex: "0 0 auto",
                        }}
                    />
                }
            />
        </Box>
    );
}
